import { Injectable } from '@angular/core';

enum ThemeType {
  dark = 'dark',
  default = 'default',
}

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  currentTheme = ThemeType.default;
  private storageKey = 'current_theme';

  constructor() {}

  private reverseTheme(theme: string): ThemeType {
    return theme === ThemeType.dark ? ThemeType.default : ThemeType.dark;
  }

  private removeUnusedTheme(theme: ThemeType): void {
    document.documentElement.classList.remove(theme);
    const removedThemeStyle = document.getElementById(theme);
    if (removedThemeStyle) {
      document.head.removeChild(removedThemeStyle);
    }
  }

  private loadCss(href: string, id: string): Promise<Event> {
    return new Promise((resolve, reject) => {
      const style = document.createElement('link');
      style.rel = 'stylesheet';
      style.href = href;
      style.id = id;
      style.media = 'print';
      style.onload = (e) => {
        style.media = 'all';
        resolve(e);
      };
      style.onerror = reject;
      document.head.append(style);
    });
  }

  public loadTheme(firstLoad = true): Promise<Event> {
    const theme = this.currentTheme;
    if (firstLoad) {
      document.documentElement.classList.add(theme);
    }
    return new Promise<Event>((resolve, reject) => {
      this.loadCss(`${theme}.css`, theme).then(
        (e) => {
          if (!firstLoad) {
            document.documentElement.classList.add(theme);
          }
          this.removeUnusedTheme(this.reverseTheme(theme));
          resolve(e);
        },
        (e) => reject(e)
      );
    });
  }

  public toggleTheme(): Promise<Event> {
    this.currentTheme = this.reverseTheme(this.currentTheme);
    localStorage.setItem(this.storageKey, this.currentTheme);
    return this.loadTheme(false);
  }

  public initOnAppStart(): void {
    const saved = localStorage.getItem(this.storageKey) as ThemeType | null;
    if (saved === ThemeType.dark || saved === ThemeType.default) {
      this.currentTheme = saved;
    }
    document.documentElement.classList.add(this.currentTheme);
    const exists = document.getElementById(this.currentTheme);
    if (!exists) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = `${this.currentTheme}.css`;
      link.id = this.currentTheme;
      link.media = 'print';
      link.onload = () => {
        link.media = 'all';
        this.removeUnusedTheme(this.reverseTheme(this.currentTheme));
      };
      document.head.append(link);
    }
  }
}
